import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  flowMax,
} from 'ad-hok'
import { addEffectOnMount } from 'ad-hok-utils'
import cx from 'classnames'
import { FC, MutableRefObject } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import {
  BrandFeed,
  CategoryFeed,
  FeaturedUsersFeed,
  UserFeed,
  isFeedItemEdit,
  isFeedItemFeaturedBrands,
  isFeedItemFeaturedProducts,
  isFeedItemFeaturedUsers,
  isFeedItemMedia,
  isUserFeed,
} from 'shared/src/api/types'
import { addLocation } from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { scrollToId } from 'shared/src/utils/scrollToId'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { NAVBAR_Z_INDEX } from 'utils/zIndex'
import { ConstrainWidth } from '../../../utils/layout'
import { addMenuPage } from '../../menu'
import { addRegisterPage } from '../../register'
import { addFeedContext, addFeedContextProvider } from '../context'
import { FeatureContainer } from './FeatureContainer'
import { Media, MediaState, invertState } from './Media'
import { addRodeoButtonTutorial } from './rodeoButtonTutorial'
import { indexIsFlipped } from './util'
import { FeedFeaturedCreator } from 'components/feed/fluid/FeedFeaturedCreator'
import { FeedFeaturedProduct } from 'components/feed/fluid/FeedFeaturedProduct'
import { BrandFeature } from './BrandFeature'
import { EditFeedItem } from './EditFeedItem'
import { Loading } from 'components/Loading'

const extractActiveMediaIdFromUrlHash = (hash: string): number | null =>
  hash ? parseInt(hash.substring(1)) : null

const useStyles = createUseStyles({
  roundupButton: {
    position: 'absolute',
    top: 16,
    right: 34,
    zIndex: NAVBAR_Z_INDEX,
  },
  filterContainer: {
    position: 'absolute',
    top: 58,
    height: 48,
    left: 48,
    zIndex: NAVBAR_Z_INDEX,
  },
  noMediaPlaceholder: {
    height: '32dvh',
  },
  mediaContainer: {
    height: '100%',
    width: '100%',
  },
  mediaItem: {
    scrollSnapAlign: 'start',
    height: '100%',
  },
  featuredContainer: {
    paddingTop: 45,
    justifyContent: 'center',
  },
  featured: {
    height: '60%',
  },
})

interface Props {
  feed: UserFeed | CategoryFeed | BrandFeed | FeaturedUsersFeed
  scrollableContainerRef: MutableRefObject<HTMLDivElement | null>
  onClickCreator?: () => void
  requestNextPage: () => void
  mediaIds: number[]
  paginationLoading?: boolean
}

export const Fluid: FC<Props> = flowMax(
  addDisplayName('Fluid'),
  addApi,
  addFeedContextProvider,
  addLocation,
  addNavigationState('feedState', 'setFeedState', typedAs<MediaState>('rodeo')),
  addEffectOnMount(({ setFeedState, feed, location: { hash } }) => () => {
    const activeMediaIdOnMount = extractActiveMediaIdFromUrlHash(hash)

    const feedState =
      (activeMediaIdOnMount !== null && !isUserFeed(feed)) ||
      (isUserFeed(feed) && !feed.user.defaultToFullScreen)
        ? 'rodeo'
        : 'feed'
    setFeedState(feedState)

    if (!activeMediaIdOnMount) {
      return
    }

    scrollToId(activeMediaIdOnMount.toString())
  }),
  addFeedContext,
  addProps({
    lastRequestedIndexRef: typedAs<{ current: number | null }>({
      current: null,
    }),
  }),
  addEffect(
    ({ requestNextPage, mediaIds, visibleMediaId, lastRequestedIndexRef }) =>
      () => {
        const visibleMediaIndex = mediaIds.findIndex(
          (item) => item === visibleMediaId
        )

        if (visibleMediaIndex === -1) {
          return
        }

        const isWithinLast6 = visibleMediaIndex >= mediaIds.length - 6

        if (
          isWithinLast6 &&
          lastRequestedIndexRef.current !== visibleMediaIndex
        ) {
          lastRequestedIndexRef.current = visibleMediaIndex
          requestNextPage()
        }
      },
    ['requestNextPage', 'visibleMediaId', 'mediaIds']
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  addHandlers(
    {
      onToggleState:
        ({ feedState, setFeedState }) =>
        () => {
          setFeedState(invertState(feedState))
        },
    },
    ['feedState', 'setFeedState']
  ),
  addMenuPage,
  addRegisterPage,
  addFeedContext,
  addHandlers(
    {
      scrollToTop:
        ({ scrollableContainerRef }) =>
        () => {
          scrollableContainerRef.current?.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        },
    },
    ['scrollableContainerRef']
  ),
  addRodeoButtonTutorial,
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    feed,
    feed: { media },
    feedState,
    onToggleState,
    scrollToTop,
    onClickCreator,
    scrollableContainerRef,
    classes,
    paginationLoading,
  }) => (
    <>
      {media.length === 0 && <div className={classes.noMediaPlaceholder} />}
      {media.map((item, index) => (
        <ConstrainWidth
          key={isFeedItemMedia(item) ? `media-${item.id}` : `featured-${index}`}
          className={classes.mediaContainer}
        >
          {isFeedItemMedia(item) ? (
            <Media
              media={item}
              state={feedState}
              onToggleState={onToggleState}
              onClickCreator={
                isUserFeed(feed) ? onClickCreator ?? scrollToTop : undefined
              }
              isFlipped={indexIsFlipped(index)}
              scrollableContainerElement={scrollableContainerRef.current}
              className={classes.mediaItem}
            />
          ) : isFeedItemEdit(item) ? (
            <EditFeedItem
              edit={item}
              onClickCreator={onClickCreator}
              className={classes.mediaItem}
            />
          ) : (
            <FeatureContainer
              className={cx(classes.mediaItem, classes.featuredContainer)}
            >
              {isFeedItemFeaturedUsers(item) ? (
                <FeedFeaturedCreator creatorFeatures={item.users} />
              ) : isFeedItemFeaturedProducts(item) ? (
                <FeedFeaturedProduct products={item.products} />
              ) : isFeedItemFeaturedBrands(item) ? (
                <BrandFeature
                  brandFeatures={item.brands}
                  className={classes.featured}
                />
              ) : null}
            </FeatureContainer>
          )}
        </ConstrainWidth>
      ))}
      {paginationLoading && (
        <ConstrainWidth
          key="pagination-loading"
          className={classes.mediaContainer}
        >
          <Loading state="loading"></Loading>
        </ConstrainWidth>
      )}
    </>
  )
)
